import { AppLanguage, detectLanguage } from './language';

const en = {
  'signup.acceptTerms':
    'Please accept the Terms of Service and Privacy Policy.',
  'signup.completeVerification': 'Please complete the verification challenge.',
  'signup.success': 'Sign up successful! Redirecting...',
  'signup.checkEmail': 'Please check your email for verification.',
  'signup.failed': 'Signup failed. Please try again.',
  'login.success': 'Login successful! Redirecting...',
  'login.failed': 'Login failed. Please try again.',
  'forgot.failed': 'Could not send the reset email. Please try again.',
  'reset.success': 'Password reset successful.',
  'reset.redirecting': 'Redirecting to login page...',
  'reset.failed': 'Could not reset your password. Please try again.',
  'errors.REQUIRED_FIELDS_MISSING': 'Please fill in all required fields.',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE':
    'Signup verification is temporarily unavailable. Please try again later.',
  'errors.VERIFICATION_FAILED': 'Verification failed. Please try again.',
  'errors.EMAIL_ALREADY_REGISTERED':
    'An account with this email already exists.',
  'errors.SIGNUP_FAILED':
    'Could not create your account. Please try again.',
  'errors.EMAIL_PASSWORD_REQUIRED':
    'Please enter your email and password.',
  'errors.INVALID_CREDENTIALS': 'Invalid email or password.',
  'errors.UNVERIFIED_EMAIL':
    'Please verify your email before logging in.',
  'errors.LOGIN_FAILED': 'Could not log you in. Please try again.',
  'errors.PASSWORD_RESET_REQUEST_FAILED':
    'Could not send the reset email. Please try again.',
  'errors.INVALID_RESET_TOKEN':
    'This reset link is invalid or has expired. Request a new one.',
  'errors.PASSWORD_RESET_FAILED':
    'Could not reset your password. Please try again.',
} as const;

export type AuthMessageKey = keyof typeof en;

const zh: Record<AuthMessageKey, string> = {
  'signup.acceptTerms': '请接受服务条款和隐私政策。',
  'signup.completeVerification': '请完成验证挑战。',
  'signup.success': '注册成功！正在跳转...',
  'signup.checkEmail': '请查看邮箱完成验证。',
  'signup.failed': '注册失败，请重试。',
  'login.success': '登录成功！正在跳转...',
  'login.failed': '登录失败，请重试。',
  'forgot.failed': '无法发送重置邮件，请重试。',
  'reset.success': '密码重置成功。',
  'reset.redirecting': '正在跳转到登录页面...',
  'reset.failed': '无法重置密码，请重试。',
  'errors.REQUIRED_FIELDS_MISSING': '请填写所有必填字段。',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE': '注册验证暂时不可用，请稍后重试。',
  'errors.VERIFICATION_FAILED': '验证失败，请重试。',
  'errors.EMAIL_ALREADY_REGISTERED': '该邮箱已注册账号。',
  'errors.SIGNUP_FAILED': '无法创建账号，请重试。',
  'errors.EMAIL_PASSWORD_REQUIRED': '请输入邮箱和密码。',
  'errors.INVALID_CREDENTIALS': '邮箱或密码错误。',
  'errors.UNVERIFIED_EMAIL': '请先验证邮箱再登录。',
  'errors.LOGIN_FAILED': '无法登录，请重试。',
  'errors.PASSWORD_RESET_REQUEST_FAILED': '无法发送重置邮件，请重试。',
  'errors.INVALID_RESET_TOKEN': '此重置链接无效或已过期，请重新申请。',
  'errors.PASSWORD_RESET_FAILED': '无法重置密码，请重试。',
};

const es: Record<AuthMessageKey, string> = {
  'signup.acceptTerms':
    'Acepta los Términos de Servicio y la Política de Privacidad.',
  'signup.completeVerification': 'Completa el desafío de verificación.',
  'signup.success': '¡Registro correcto! Redirigiendo...',
  'signup.checkEmail': 'Revisa tu correo para verificar la cuenta.',
  'signup.failed': 'No se pudo completar el registro. Inténtalo de nuevo.',
  'login.success': '¡Inicio de sesión correcto! Redirigiendo...',
  'login.failed': 'No se pudo iniciar sesión. Inténtalo de nuevo.',
  'forgot.failed':
    'No se pudo enviar el correo de restablecimiento. Inténtalo de nuevo.',
  'reset.success': 'Contraseña restablecida correctamente.',
  'reset.redirecting': 'Redirigiendo a la página de inicio de sesión...',
  'reset.failed':
    'No se pudo restablecer la contraseña. Inténtalo de nuevo.',
  'errors.REQUIRED_FIELDS_MISSING': 'Completa todos los campos obligatorios.',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE':
    'La verificación de registro no está disponible temporalmente. Inténtalo más tarde.',
  'errors.VERIFICATION_FAILED': 'La verificación falló. Inténtalo de nuevo.',
  'errors.EMAIL_ALREADY_REGISTERED':
    'Ya existe una cuenta con este correo.',
  'errors.SIGNUP_FAILED':
    'No se pudo crear tu cuenta. Inténtalo de nuevo.',
  'errors.EMAIL_PASSWORD_REQUIRED': 'Introduce tu correo y contraseña.',
  'errors.INVALID_CREDENTIALS': 'Correo o contraseña incorrectos.',
  'errors.UNVERIFIED_EMAIL':
    'Verifica tu correo antes de iniciar sesión.',
  'errors.LOGIN_FAILED': 'No se pudo iniciar sesión. Inténtalo de nuevo.',
  'errors.PASSWORD_RESET_REQUEST_FAILED':
    'No se pudo enviar el correo de restablecimiento. Inténtalo de nuevo.',
  'errors.INVALID_RESET_TOKEN':
    'Este enlace de restablecimiento no es válido o ha caducado. Solicita uno nuevo.',
  'errors.PASSWORD_RESET_FAILED':
    'No se pudo restablecer la contraseña. Inténtalo de nuevo.',
};

const fr: Record<AuthMessageKey, string> = {
  'signup.acceptTerms':
    "Veuillez accepter les Conditions d'utilisation et la Politique de confidentialité.",
  'signup.completeVerification':
    'Veuillez compléter le défi de vérification.',
  'signup.success': 'Inscription réussie ! Redirection...',
  'signup.checkEmail':
    'Veuillez vérifier votre e-mail pour confirmer votre compte.',
  'signup.failed': "L'inscription a échoué. Veuillez réessayer.",
  'login.success': 'Connexion réussie ! Redirection...',
  'login.failed': 'La connexion a échoué. Veuillez réessayer.',
  'forgot.failed':
    "Impossible d'envoyer l'e-mail de réinitialisation. Veuillez réessayer.",
  'reset.success': 'Mot de passe réinitialisé avec succès.',
  'reset.redirecting': 'Redirection vers la page de connexion...',
  'reset.failed':
    'Impossible de réinitialiser le mot de passe. Veuillez réessayer.',
  'errors.REQUIRED_FIELDS_MISSING':
    'Veuillez remplir tous les champs obligatoires.',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE':
    "La vérification d'inscription est temporairement indisponible. Réessayez plus tard.",
  'errors.VERIFICATION_FAILED':
    'La vérification a échoué. Veuillez réessayer.',
  'errors.EMAIL_ALREADY_REGISTERED':
    'Un compte existe déjà avec cet e-mail.',
  'errors.SIGNUP_FAILED':
    'Impossible de créer votre compte. Veuillez réessayer.',
  'errors.EMAIL_PASSWORD_REQUIRED':
    'Veuillez saisir votre e-mail et votre mot de passe.',
  'errors.INVALID_CREDENTIALS': 'E-mail ou mot de passe incorrect.',
  'errors.UNVERIFIED_EMAIL':
    'Veuillez vérifier votre e-mail avant de vous connecter.',
  'errors.LOGIN_FAILED':
    'Impossible de vous connecter. Veuillez réessayer.',
  'errors.PASSWORD_RESET_REQUEST_FAILED':
    "Impossible d'envoyer l'e-mail de réinitialisation. Veuillez réessayer.",
  'errors.INVALID_RESET_TOKEN':
    'Ce lien de réinitialisation est invalide ou a expiré. Demandez-en un nouveau.',
  'errors.PASSWORD_RESET_FAILED':
    'Impossible de réinitialiser le mot de passe. Veuillez réessayer.',
};

const de: Record<AuthMessageKey, string> = {
  'signup.acceptTerms':
    'Bitte akzeptiere die Nutzungsbedingungen und die Datenschutzerklärung.',
  'signup.completeVerification':
    'Bitte schließe die Verifizierungsaufgabe ab.',
  'signup.success': 'Registrierung erfolgreich! Weiterleitung...',
  'signup.checkEmail':
    'Bitte prüfe deine E-Mail zur Verifizierung.',
  'signup.failed':
    'Registrierung fehlgeschlagen. Bitte versuche es erneut.',
  'login.success': 'Anmeldung erfolgreich! Weiterleitung...',
  'login.failed': 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.',
  'forgot.failed':
    'Die Reset-E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.',
  'reset.success': 'Passwort erfolgreich zurückgesetzt.',
  'reset.redirecting': 'Weiterleitung zur Anmeldeseite...',
  'reset.failed':
    'Das Passwort konnte nicht zurückgesetzt werden. Bitte versuche es erneut.',
  'errors.REQUIRED_FIELDS_MISSING':
    'Bitte fülle alle Pflichtfelder aus.',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE':
    'Die Registrierungsverifizierung ist vorübergehend nicht verfügbar. Bitte später erneut versuchen.',
  'errors.VERIFICATION_FAILED':
    'Verifizierung fehlgeschlagen. Bitte versuche es erneut.',
  'errors.EMAIL_ALREADY_REGISTERED':
    'Für diese E-Mail existiert bereits ein Konto.',
  'errors.SIGNUP_FAILED':
    'Dein Konto konnte nicht erstellt werden. Bitte versuche es erneut.',
  'errors.EMAIL_PASSWORD_REQUIRED':
    'Bitte gib E-Mail und Passwort ein.',
  'errors.INVALID_CREDENTIALS': 'E-Mail oder Passwort ist falsch.',
  'errors.UNVERIFIED_EMAIL':
    'Bitte verifiziere deine E-Mail vor der Anmeldung.',
  'errors.LOGIN_FAILED':
    'Anmeldung nicht möglich. Bitte versuche es erneut.',
  'errors.PASSWORD_RESET_REQUEST_FAILED':
    'Die Reset-E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.',
  'errors.INVALID_RESET_TOKEN':
    'Dieser Reset-Link ist ungültig oder abgelaufen. Fordere einen neuen an.',
  'errors.PASSWORD_RESET_FAILED':
    'Das Passwort konnte nicht zurückgesetzt werden. Bitte versuche es erneut.',
};

const pt: Record<AuthMessageKey, string> = {
  'signup.acceptTerms':
    'Aceite os Termos de Serviço e a Política de Privacidade.',
  'signup.completeVerification': 'Conclua o desafio de verificação.',
  'signup.success': 'Cadastro realizado! Redirecionando...',
  'signup.checkEmail':
    'Verifique seu e-mail para confirmar a conta.',
  'signup.failed':
    'Não foi possível concluir o cadastro. Tente novamente.',
  'login.success': 'Login realizado! Redirecionando...',
  'login.failed': 'Não foi possível fazer login. Tente novamente.',
  'forgot.failed':
    'Não foi possível enviar o e-mail de redefinição. Tente novamente.',
  'reset.success': 'Senha redefinida com sucesso.',
  'reset.redirecting': 'Redirecionando para a página de login...',
  'reset.failed':
    'Não foi possível redefinir a senha. Tente novamente.',
  'errors.REQUIRED_FIELDS_MISSING':
    'Preencha todos os campos obrigatórios.',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE':
    'A verificação de cadastro está temporariamente indisponível. Tente mais tarde.',
  'errors.VERIFICATION_FAILED': 'A verificação falhou. Tente novamente.',
  'errors.EMAIL_ALREADY_REGISTERED':
    'Já existe uma conta com este e-mail.',
  'errors.SIGNUP_FAILED':
    'Não foi possível criar sua conta. Tente novamente.',
  'errors.EMAIL_PASSWORD_REQUIRED': 'Informe seu e-mail e senha.',
  'errors.INVALID_CREDENTIALS': 'E-mail ou senha incorretos.',
  'errors.UNVERIFIED_EMAIL':
    'Verifique seu e-mail antes de fazer login.',
  'errors.LOGIN_FAILED':
    'Não foi possível fazer login. Tente novamente.',
  'errors.PASSWORD_RESET_REQUEST_FAILED':
    'Não foi possível enviar o e-mail de redefinição. Tente novamente.',
  'errors.INVALID_RESET_TOKEN':
    'Este link de redefinição é inválido ou expirou. Solicite um novo.',
  'errors.PASSWORD_RESET_FAILED':
    'Não foi possível redefinir a senha. Tente novamente.',
};

const ja: Record<AuthMessageKey, string> = {
  'signup.acceptTerms':
    '利用規約とプライバシーポリシーに同意してください。',
  'signup.completeVerification': '認証チェックを完了してください。',
  'signup.success': '登録が完了しました！リダイレクトしています...',
  'signup.checkEmail': '確認メールをご確認ください。',
  'signup.failed': '登録に失敗しました。もう一度お試しください。',
  'login.success': 'ログインしました！リダイレクトしています...',
  'login.failed': 'ログインに失敗しました。もう一度お試しください。',
  'forgot.failed':
    'リセットメールを送信できませんでした。もう一度お試しください。',
  'reset.success': 'パスワードをリセットしました。',
  'reset.redirecting': 'ログインページにリダイレクトしています...',
  'reset.failed':
    'パスワードをリセットできませんでした。もう一度お試しください。',
  'errors.REQUIRED_FIELDS_MISSING': '必須項目をすべて入力してください。',
  'errors.SIGNUP_VERIFICATION_UNAVAILABLE':
    '登録の認証は一時的に利用できません。しばらくしてからお試しください。',
  'errors.VERIFICATION_FAILED':
    '認証に失敗しました。もう一度お試しください。',
  'errors.EMAIL_ALREADY_REGISTERED':
    'このメールアドレスのアカウントはすでに存在します。',
  'errors.SIGNUP_FAILED':
    'アカウントを作成できませんでした。もう一度お試しください。',
  'errors.EMAIL_PASSWORD_REQUIRED':
    'メールアドレスとパスワードを入力してください。',
  'errors.INVALID_CREDENTIALS':
    'メールアドレスまたはパスワードが正しくありません。',
  'errors.UNVERIFIED_EMAIL':
    'ログインする前にメールアドレスを確認してください。',
  'errors.LOGIN_FAILED':
    'ログインできませんでした。もう一度お試しください。',
  'errors.PASSWORD_RESET_REQUEST_FAILED':
    'リセットメールを送信できませんでした。もう一度お試しください。',
  'errors.INVALID_RESET_TOKEN':
    'このリセットリンクは無効か期限切れです。新しいリンクを申請してください。',
  'errors.PASSWORD_RESET_FAILED':
    'パスワードをリセットできませんでした。もう一度お試しください。',
};

const MESSAGES: Record<AppLanguage, Record<AuthMessageKey, string>> = {
  en,
  zh,
  es,
  fr,
  de,
  pt,
  ja,
};

export function authMessage(
  key: AuthMessageKey,
  language: AppLanguage = detectLanguage()
): string {
  return MESSAGES[language]?.[key] ?? en[key];
}

const isAuthMessageKey = (value: string): value is AuthMessageKey =>
  Object.prototype.hasOwnProperty.call(en, value);

/** Prefer a translated backend error code; fall back to the page's message. */
export function authErrorMessage(
  error: unknown,
  fallbackKey: AuthMessageKey,
  language: AppLanguage = detectLanguage()
): string {
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code === 'string') {
    const key = `errors.${code}`;
    if (isAuthMessageKey(key)) return authMessage(key, language);
  }
  return authMessage(fallbackKey, language);
}
